// Service for fetching markdown content for individual projects
import logger from '../utils/logger';
import { transformMarkdownImages } from '../utils/markdownImageTransform';

/**
 * Fetch description assets for a project
 * @param {string} projectFolderId - Google Drive folder ID for the project
 * @returns {Promise<Object|null>} Description assets mapping or null if failed
 */
export const fetchDescriptionAssets = async (projectFolderId) => {
  if (!projectFolderId) {
    logger.warn('No project folder ID provided for description assets fetch');
    return null;
  }

  try {
    logger.log(
      `📁 Fetching description assets for project folder: ${projectFolderId}`
    );

    const response = await fetch(
      `/.netlify/functions/fetch-description-assets?projectFolderId=${projectFolderId}`
    );

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch (jsonError) {
        errorData = await response.text();
      }
      logger.error(
        'Description assets fetch error:',
        response.status,
        errorData
      );
      return null;
    }

    const result = await response.json();

    if (result.success && result.assets) {
      logger.log(
        `✅ Successfully fetched ${
          Object.keys(result.assets).length
        } description assets`
      );
      return result.assets;
    } else {
      logger.error('Invalid response from description assets fetch:', result);
      return null;
    }
  } catch (error) {
    logger.error('Error fetching description assets:', error);
    return null;
  }
};

/**
 * Fetch markdown content for a project by folder ID
 * @param {string} projectFolderId - Google Drive folder ID for the project
 * @returns {Promise<string|null>} Markdown content or null if failed
 */
export const fetchMarkdownContent = async (projectFolderId) => {
  if (!projectFolderId) {
    logger.warn('No project folder ID provided for markdown fetch');
    return null;
  }

  try {
    logger.log(
      `📄 Fetching markdown content for project folder: ${projectFolderId}`
    );

    const response = await fetch(
      `/.netlify/functions/fetch-markdown?projectFolderId=${projectFolderId}`
    );

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch (jsonError) {
        // If response is not JSON, get the text content
        errorData = await response.text();
      }
      logger.error('Markdown fetch error:', response.status, errorData);
      return null;
    }

    const result = await response.json();

    if (result.success && result.content) {
      logger.log('✅ Successfully fetched markdown content');

      // Fetch description assets and transform images
      const descriptionAssets = await fetchDescriptionAssets(projectFolderId);
      if (descriptionAssets) {
        const transformedContent = transformMarkdownImages(
          result.content,
          descriptionAssets
        );
        logger.log('✅ Transformed markdown images');
        return transformedContent;
      }

      return result.content;
    } else {
      logger.error('Invalid response from markdown fetch:', result);
      return null;
    }
  } catch (error) {
    logger.error('Error fetching markdown content:', error);
    return null;
  }
};
